// gameengine/gameMissions.js

/**
 * Processes active mission progress and completions
 * @param {Array} activeMissions - Array of active missions
 * @param {Array} squad - Current squad for calculating success rates
 * @returns {Object} Updated missions with completed missions separated
 */
export const processMissions = (activeMissions, squad) => {
  const ongoing = [];
  const completed = [];

  activeMissions.forEach(mission => {
    if (mission.days_remaining > 0) {
      const daysRemaining = mission.days_remaining - 1;
      
      if (daysRemaining === 0) {
        const missionResult = resolveMission(mission, squad);
        completed.push({
          ...mission,
          days_remaining: 0,
          status: 'Completed',
          success: missionResult.success,
          casualties: missionResult.casualties,
          rewards: missionResult.rewards
        });
      } else {
        ongoing.push({ ...mission, days_remaining: daysRemaining });
      }
    }
  });

  return { ongoing, completed };
};

/**
 * Resolves a mission to determine success/failure and outcomes
 * @param {Object} mission - Mission to resolve
 * @param {Array} squad - Full squad array for reference
 * @returns {Object} Mission result with success, casualties, and rewards
 */
export const resolveMission = (mission, squad) => {
  const missionSquad = mission.squad || [];
  const avgCombat = missionSquad.reduce((sum, member) => {
    const squadMember = squad.find(s => s.id === member.id);
    return sum + (squadMember?.stats?.combat || 50);
  }, 0) / (missionSquad.length || 1);

  const baseSuccessRate = mission.risks?.successRate || 70;
  const squadModifier = (avgCombat - 50) / 2;
  const finalSuccessRate = Math.max(10, Math.min(95, baseSuccessRate + squadModifier));

  const success = Math.random() * 100 < finalSuccessRate;

  const casualties = calculateCasualties(mission, missionSquad, success);
  const rewards = calculateRewards(mission, success);

  return {
    success,
    casualties,
    rewards,
    finalSuccessRate
  };
};

/**
 * Calculates casualties from a mission
 * @param {Object} mission - Mission object
 * @param {Array} missionSquad - Squad members on mission
 * @param {boolean} success - Whether mission succeeded
 * @returns {Array} Casualties with injury details
 */
const calculateCasualties = (mission, missionSquad, success) => {
  const casualties = [];
  const casualtyRisk = getCasualtyRiskValue(mission.risks?.casualty);
  const riskMultiplier = success ? 0.5 : 1.5;

  missionSquad.forEach(member => {
    if (Math.random() * 100 < casualtyRisk * riskMultiplier) {
      const healthLoss = success 
        ? Math.floor(Math.random() * 20) + 10
        : Math.floor(Math.random() * 30) + 20;
      
      casualties.push({
        ...member,
        injured: true,
        healthLoss
      });
    }
  });

  return casualties;
};

/**
 * Calculates rewards from a mission
 * @param {Object} mission - Mission object
 * @param {boolean} success - Whether mission succeeded
 * @returns {Object} Reward amounts
 */
const calculateRewards = (mission, success) => {
  const rewardMultiplier = success ? 1 : 0.3;
  
  return {
    funds: Math.floor((mission.rewards?.funds || 0) * rewardMultiplier),
    intel: Math.floor((mission.rewards?.intel || 0) * rewardMultiplier),
    supplies: Math.floor((mission.rewards?.supplies || 0) * rewardMultiplier),
    xp: Math.floor((mission.rewards?.xp || 0) * rewardMultiplier)
  };
};

/**
 * Converts text-based casualty risk to numeric value
 * @param {string} risk - Risk level
 * @returns {number} Percentage chance of casualty per squad member
 */
export const getCasualtyRiskValue = (risk) => {
  const riskMap = {
    'None': 0,
    'Minimal': 5,
    'Low': 15,
    'Medium': 25,
    'High': 40,
    'Very High': 60,
    'Extreme': 80
  };
  return riskMap[risk] || 25;
};

/**
 * Calculates mission success rate
 * @param {Object} mission - Mission object
 * @param {Array} squad - Full squad array
 * @returns {number} Success rate percentage
 */
export const calculateSuccessRate = (mission, squad) => {
  const missionSquad = mission.squad || [];
  if (missionSquad.length === 0) return 0;

  const avgCombat = missionSquad.reduce((sum, member) => {
    const squadMember = squad.find(s => s.id === member.id);
    return sum + (squadMember?.stats?.combat || 50);
  }, 0) / missionSquad.length;

  const baseSuccessRate = mission.risks?.successRate || 70;
  const squadModifier = (avgCombat - 50) / 2;
  
  return Math.max(10, Math.min(95, baseSuccessRate + squadModifier));
};